import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderKanban, Building2, Users, ShieldCheck,
  Award, FileCheck, MessageSquare,
  Clock, Plus, ChevronRight,
  AlertTriangle, AlertCircle, UserX
} from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import api from '../../services/apiService';

// Stat Summary Card Component
const StatSummaryCard = ({ icon: Icon, value, label, badge, badgeColor, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer"
  >
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
        <Icon className="h-5 w-5 text-slate-600" />
      </div>
      {badge && (
        <span className={`px-2 py-0.5 text-[9px] font-black uppercase tracking-wider rounded-md ${badgeColor || 'bg-slate-100 text-slate-600'}`}>
          {badge}
        </span>
      )}
    </div>
    <p className="text-2xl font-black text-slate-900">{value}</p>
    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{label}</p>
  </div>
);

// Project Row Component
const ProjectRow = ({ project, onClick }) => (
  <div
    onClick={onClick}
    className="flex items-center justify-between p-4 rounded-xl bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors border border-orange-200"
  >
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
        <FolderKanban className="h-5 w-5 text-orange-500" />
      </div>
      <div>
        <p className="text-sm font-black text-slate-900">{project.name}</p>
        <p className="text-[11px] text-slate-500 font-medium">{project.code}</p>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-50">
        <span className="text-[9px] font-bold text-blue-600 uppercase">Technicians</span>
        <span className="text-[11px] font-black text-blue-700">{project.technicians_count || 0}</span>
      </div>
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-100">
        <span className="text-[9px] font-bold text-slate-500 uppercase">Companies</span>
        <span className="text-[11px] font-black text-slate-600">{project.companies_count || 0}</span>
      </div>
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-50">
        <span className="text-[9px] font-bold text-emerald-600 uppercase">Authorized</span>
        <span className="text-[11px] font-black text-emerald-700">{project.authorized_count || 0}</span>
      </div>
    </div>
  </div>
);

// Nomination List Item Component
const NominationListItem = ({ nomination, onClick, formatDate, getStatusStyle }) => {
  const style = getStatusStyle(nomination.status);
  return (
    <div
      onClick={onClick}
      className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
          <Award className="h-4 w-4 text-violet-600" />
        </div>
        <div>
          <p className="text-xs font-black text-slate-900">{nomination.user_name}</p>
          <p className="text-[10px] text-slate-500">{nomination.authorization_level_code}</p>
        </div>
      </div>
      <div className="text-right">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-md ${style.bg} ${style.text}`}>
          <span className={`w-1 h-1 rounded-full ${style.dot}`} />
          {nomination.status.replace('_', ' ')}
        </span>
        <p className="text-[10px] text-slate-400 mt-1">{formatDate(nomination.created_at)}</p>
      </div>
    </div>
  );
};

// Quick Action Card Component
const QuickActionCard = ({ icon: Icon, title, subtitle, color, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer"
  >
    <div className="flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{subtitle}</p>
        <p className="text-sm font-black text-slate-900">{title}</p>
      </div>
    </div>
  </div>
);

const OEMDashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_projects: 0,
    active_projects: 0,
    total_companies: 0,
    total_technicians: 0,
    pending_nominations: 0,
    recent_toolboxes: 0,
  });
  const [recentNominations, setRecentNominations] = useState([]);
  const [projectsSummary, setProjectsSummary] = useState([]);

  // User role and certificate alerts for non-master users
  const [userRoles, setUserRoles] = useState([]);
  const [isMasterOnly, setIsMasterOnly] = useState(true);
  const [certificateAlerts, setCertificateAlerts] = useState({
    total: 0,
    expired: 0,
    under30Days: 0,
    between30And60Days: 0,
    affectedUsers: 0,
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchCertificateAlerts = async (projects) => {
    try {
      const alertPromises = projects.map(project =>
        api.get(`/oem/projects/${project.id}/certificate-alerts/`)
          .then(res => res.data)
          .catch(() => null)
      );

      const results = await Promise.all(alertPromises);
      const validResults = results.filter(r => r !== null);

      let expired = 0;
      let under30Days = 0;
      let between30And60Days = 0;
      const affectedUserIds = new Set();

      validResults.forEach(projectData => {
        expired += projectData.expired_count || 0;
        under30Days += projectData.expiring_30_count || 0;
        between30And60Days += projectData.expiring_60_count || 0;

        if (projectData.alerts && projectData.alerts.length > 0) {
          projectData.alerts.forEach(userAlert => {
            if (userAlert.alerts && userAlert.alerts.length > 0) {
              affectedUserIds.add(userAlert.user_id);
            }
          });
        }
      });

      const total = expired + under30Days + between30And60Days;

      setCertificateAlerts({
        total,
        expired,
        under30Days,
        between30And60Days,
        affectedUsers: affectedUserIds.size,
      });
    } catch (err) {
      console.error('Error fetching certificate alerts:', err);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      try {
        const teamResponse = await api.get('/oem/dashboard/team/');
        const roles = teamResponse.data.my_roles || [];
        setUserRoles(roles);

        const hasMasterRole = roles.some(r => r.role === 'master');
        const hasOnlyRestrictedRoles = roles.length > 0 && !hasMasterRole;

        setIsMasterOnly(!hasOnlyRestrictedRoles);
      } catch (err) {
        setIsMasterOnly(true);
      }

      const response = await api.get('/oem/dashboard/master/');
      setStats(response.data.stats || {});
      setRecentNominations(response.data.recent_nominations || []);
      setProjectsSummary(response.data.projects_summary || []);

      const projectsResponse = await api.get('/oem/projects/');
      if (projectsResponse.data && projectsResponse.data.length > 0) {
        await fetchCertificateAlerts(projectsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching OEM dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusStyle = (status) => {
    const styles = {
      pending: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
      submitted: { bg: 'bg-blue-50', text: 'text-blue-600', dot: 'bg-blue-500' },
      under_review: { bg: 'bg-amber-50', text: 'text-amber-600', dot: 'bg-amber-500' },
      approved: { bg: 'bg-emerald-50', text: 'text-emerald-600', dot: 'bg-emerald-500' },
      rejected: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-500' },
    };
    return styles[status] || styles.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm">Loading dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-5">
      {/* Action Buttons */}
      {isMasterOnly && (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => navigate('/oem/projects/create')}
            className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 flex items-center gap-1.5 font-bold uppercase tracking-wide text-[10px] transition-all shadow-lg shadow-orange-500/20"
          >
            <Plus className="h-3.5 w-3.5" />
            New Project
          </button>
          <button
            onClick={() => navigate('/oem/companies')}
            className="h-9 px-4 bg-slate-50 text-slate-700 rounded-xl border border-orange-200 hover:bg-orange-50 hover:border-orange-300 flex items-center gap-1.5 font-bold text-xs transition-colors"
          >
            <Building2 className="h-3.5 w-3.5" />
            Add Company
          </button>
        </div>
      )}

      {/* Certificate Alerts - for team members */}
      {!isMasterOnly && (
        <div className="grid grid-cols-5 gap-3">
          <StatSummaryCard
            icon={AlertCircle}
            value={certificateAlerts.total}
            label="Total Alerts"
            onClick={() => navigate('/oem/certificates')}
          />
          <StatSummaryCard
            icon={AlertTriangle}
            value={certificateAlerts.expired}
            label="Expired"
            badge={certificateAlerts.expired > 0 ? 'URGENT' : null}
            badgeColor="bg-red-100 text-red-600"
            onClick={() => navigate('/oem/certificates?status=expired')}
          />
          <StatSummaryCard
            icon={Clock}
            value={certificateAlerts.under30Days}
            label="< 30 Days"
            badge={certificateAlerts.under30Days > 0 ? 'SOON' : null}
            badgeColor="bg-amber-100 text-amber-600"
            onClick={() => navigate('/oem/certificates?status=expiring_soon')}
          />
          <StatSummaryCard
            icon={Clock}
            value={certificateAlerts.between30And60Days}
            label="30-60 Days"
            onClick={() => navigate('/oem/certificates?status=expiring_60')}
          />
          <StatSummaryCard
            icon={UserX}
            value={certificateAlerts.affectedUsers}
            label="Affected Users"
            onClick={() => navigate('/oem/certificates')}
          />
        </div>
      )}

      {/* Stats Grid */}
      <div className={`grid gap-3 ${isMasterOnly ? 'grid-cols-4' : 'grid-cols-2'}`}>
        {isMasterOnly && (
          <>
            <StatSummaryCard
              icon={FolderKanban}
              value={stats.total_projects}
              label="Projects"
              badge={`${stats.active_projects} active`}
              badgeColor="bg-emerald-100 text-emerald-600"
              onClick={() => navigate('/oem/projects')}
            />
            <StatSummaryCard
              icon={Building2}
              value={stats.total_companies}
              label="Companies"
              onClick={() => navigate('/oem/companies')}
            />
          </>
        )}
        <StatSummaryCard
          icon={ShieldCheck}
          value={stats.total_technicians}
          label="Authorized"
          onClick={() => navigate('/oem/nominations')}
        />
        <StatSummaryCard
          icon={Clock}
          value={stats.pending_nominations}
          label="Pending"
          badge={stats.pending_nominations > 0 ? 'REVIEW' : null}
          badgeColor="bg-amber-100 text-amber-600"
          onClick={() => navigate('/oem/nominations?status=submitted')}
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-3 gap-5">
        {/* Recent Projects */}
        <div className="col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                <FolderKanban className="h-4 w-4 text-orange-500" />
              </div>
              <h2 className="text-sm font-black text-slate-900">{isMasterOnly ? 'RECENT PROJECTS' : 'YOUR PROJECTS'}</h2>
            </div>
            <button
              onClick={() => navigate('/oem/projects')}
              className="text-[10px] font-bold text-orange-500 hover:text-orange-600 flex items-center gap-0.5 uppercase tracking-wide transition-colors"
            >
              View all <ChevronRight className="h-3 w-3" />
            </button>
          </div>
          <div className="p-3">
            {projectsSummary.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <FolderKanban className="h-6 w-6 text-slate-400" />
                </div>
                <p className="text-xs font-bold text-slate-900 mb-1">
                  {isMasterOnly ? 'No projects yet' : 'No projects assigned'}
                </p>
                {isMasterOnly && (
                  <button
                    onClick={() => navigate('/oem/projects/create')}
                    className="text-[10px] font-bold text-orange-500 hover:text-orange-600"
                  >
                    Create your first project →
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {projectsSummary.slice(0, 5).map(project => (
                  <ProjectRow
                    key={project.id}
                    project={project}
                    onClick={() => navigate(`/oem/projects/${project.id}`)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Nominations */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                <Award className="h-4 w-4 text-violet-600" />
              </div>
              <h2 className="text-sm font-black text-slate-900">NOMINATIONS</h2>
            </div>
            <button
              onClick={() => navigate('/oem/nominations')}
              className="text-[10px] font-bold text-orange-500 hover:text-orange-600 flex items-center gap-0.5 uppercase tracking-wide transition-colors"
            >
              View all <ChevronRight className="h-3 w-3" />
            </button>
          </div>
          <div className="p-3">
            {recentNominations.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Award className="h-6 w-6 text-slate-400" />
                </div>
                <p className="text-xs font-bold text-slate-900">No nominations yet</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {recentNominations.slice(0, 6).map(nomination => (
                  <NominationListItem
                    key={nomination.id}
                    nomination={nomination}
                    onClick={() => navigate(`/oem/nominations?highlight=${nomination.id}`)}
                    formatDate={formatDate}
                    getStatusStyle={getStatusStyle}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className={`grid gap-3 ${isMasterOnly ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {isMasterOnly && (
          <QuickActionCard
            icon={ShieldCheck}
            title="Configure Matrix"
            subtitle="Authorization Levels"
            color="bg-violet-100 text-violet-600"
            onClick={() => navigate('/oem/authorization-levels')}
          />
        )}
        <QuickActionCard
          icon={FileCheck}
          title="Global Outlook"
          subtitle="Certificates"
          color="bg-emerald-100 text-emerald-600"
          onClick={() => navigate('/oem/certificates')}
        />
        <QuickActionCard
          icon={MessageSquare}
          title={`${stats.recent_toolboxes} Recent`}
          subtitle="Toolbox Talks"
          color="bg-blue-100 text-blue-600"
          onClick={() => navigate('/oem/toolboxes')}
        />
      </div>
    </div>
  );
};

export default OEMDashboard;
